import React, { FC, useState } from 'react'
import { Dropdown, MenuProps, Table, TableProps } from 'antd'
import { DecryptedPatient } from '@icure/cardinal-sdk'
import { getPatientDataFormated } from '../../../../../helpers/patientDataManipulations'
import { IconButton } from '../../../../common/IconButton'
import { deleteIcn, gearIcn, manageUserIcn, shareIcn, userIcn } from '../../../../../assets/CustomIcons'
import { DropdownRow } from '../../../../common/DropdownRow'
import { createPortal } from 'react-dom'
import { ModalPatientForm } from '../../modals/ModalPatientForm'
import { ModalSharePatient } from '../../modals/ModalSharePatient'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../../../../navigation/Router'
import { CommonPlaceholder } from '../../../../common/CommonPlaceholder'
import { ModalDeletePatient } from '../../modals/ModalDeletePatient'
import { useTranslation } from 'react-i18next'

interface DataType {
  key: string
  name: string
  birthDay: string
  phone: string
  email: string
  address: string
  patient: DecryptedPatient
}

interface PatientsTableProps {
  showSpinner: boolean
  patientList?: DecryptedPatient[]
  noMatchingSearchPatients?: boolean
}

export const PatientsTable: FC<PatientsTableProps> = ({ showSpinner, patientList, noMatchingSearchPatients }) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const [isPatientFormModalOpen, setPatientFormModalOpen] = useState(false)
  const [isPatientToBeDeleted, setPatientToBeDeleted] = useState(false)
  const [isSharePatientModalOpen, setSharePatientModalOpen] = useState(false)

  const navigate = useNavigate()

  const [actionedPatient, setActionedPatient] = useState<DecryptedPatient | undefined>(undefined)

  const getOverflowMenu = (patient: DecryptedPatient) => {
    const items: MenuProps['items'] = [
      {
        key: 'view_profile',
        label: <DropdownRow icon={userIcn} label={t('patients.table.overflowMenu.viewProfile')} />,
      },
      {
        type: 'divider',
      },
      {
        key: 'edit_patient',
        label: <DropdownRow icon={manageUserIcn} label={t('patients.table.overflowMenu.edit')} />,
      },
      {
        key: 'share_patient',
        label: <DropdownRow icon={shareIcn} label={t('patients.table.overflowMenu.share')} />,
      },
      {
        key: 'delete_patient',
        danger: true,
        label: <DropdownRow icon={deleteIcn} label={t('patients.table.overflowMenu.delete')} />,
      },
    ]

    const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
      domEvent.stopPropagation()

      switch (key) {
        case 'view_profile': {
          setActionedPatient(patient)
          navigate(routes.patient(patient.id))
          break
        }
        case 'edit_patient': {
          setActionedPatient(patient)
          setPatientFormModalOpen(true)
          break
        }
        case 'share_patient': {
          setActionedPatient(patient)
          setSharePatientModalOpen(true)
          break
        }
        case 'delete_patient': {
          setActionedPatient(patient)
          setPatientToBeDeleted(true)
          break
        }
      }
    }
    return (
      <Dropdown menu={{ items, onClick }} arrow trigger={['click']}>
        <div>
          <IconButton icon={gearIcn} />
        </div>
      </Dropdown>
    )
  }

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t('patients.table.columns.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('patients.table.columns.birthDay'),
      dataIndex: 'birthDay',
      key: 'birthDay',
    },
    {
      title: t('patients.table.columns.phone'),
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: t('patients.table.columns.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('patients.table.columns.address'),
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: t('patients.table.columns.actions'),
      dataIndex: 'overflowMenu',
      key: 'overflowMenu',
      align: 'center',
      fixed: 'right',
      render: (_, record) => getOverflowMenu(record.patient),
      onCell: (_) => ({
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation()
        },
      }),
    },
  ]

  const mappedPatients = patientList?.map((patient, index) => {
    const { userNameOneString, emailAddress, userHomeAddressOneString, userDateOfBirthOneString, phoneNumber } = getPatientDataFormated(patient, t, currentLanguage)

    return {
      key: patient.id ?? index.toString(),
      name: userNameOneString ?? '-',
      birthDay: userDateOfBirthOneString ?? '-',
      phone: phoneNumber ?? '-',
      email: emailAddress ?? '-',
      address: userHomeAddressOneString ?? '-',
      patient: patient,
    }
  })

  return (
    <>
      <Table
        scroll={{ x: 'max-content' }}
        size="middle"
        bordered={true}
        columns={columns}
        dataSource={mappedPatients}
        pagination={false}
        loading={showSpinner}
        locale={{
          emptyText: (
            <CommonPlaceholder
              borderless={true}
              title={noMatchingSearchPatients ? t('patients.table.empty.noPatientFound') : t('patients.table.empty.noPatientsYet')}
              content={noMatchingSearchPatients ? t('patients.table.empty.searchNoResults') : t('patients.table.empty.emptyHint')}
            />
          ),
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              setActionedPatient(record.patient)
              navigate(routes.patient(record.patient.id))
            },
            style: { cursor: 'pointer' },
          }
        }}
      />

      {isPatientFormModalOpen &&
        actionedPatient &&
        createPortal(
          <ModalPatientForm mode="edit" patientToEdit={actionedPatient} isVisible={isPatientFormModalOpen} onClose={() => setPatientFormModalOpen(false)} />,
          document.body,
        )}
      {isPatientToBeDeleted &&
        actionedPatient &&
        createPortal(<ModalDeletePatient isVisible={isPatientToBeDeleted} patient={actionedPatient} onClose={() => setPatientToBeDeleted(false)} />, document.body)}
      {isSharePatientModalOpen &&
        actionedPatient &&
        createPortal(<ModalSharePatient isVisible={isSharePatientModalOpen} onClose={() => setSharePatientModalOpen(false)} patientToEdit={actionedPatient} />, document.body)}
    </>
  )
}
